import Konva from 'konva';
import type { Wire } from '../../core/model/wire.js';
import { computePinPath3D } from '../../core/geometry/pin-path.js';
import { wireAsPin, WIRE_DIAMETER_MM } from '../../core/geometry/wire-path.js';
import { buildSmoothPolyline, renderBendMarks } from './pin-renderer.js';

/**
 * Renderer de un cable / hilo de conexión.
 *
 * Estilo: tira metálica de ancho uniforme (~0.48 mm) en tonos cobre.
 * Sigue la path completa del cable (incluidos puntos de doblez) y
 * suaviza cada codo con el mismo helper que un pin plano
 * (`buildSmoothPolyline`).
 *
 * El grupo lleva los attrs { nodeKind: 'wire', wireUid }; cada
 * segmento (Konva.Line) lleva también esos attrs para hit-testing.
 * Si `showBendMarks` es true, se añaden marcas clicables en cada
 * punto de doblez (con `bendIndex` y `wireUid` para el sistema de
 * eventos).
 */
export function renderWire(w: Wire, showBendMarks = false, marksVisible = true): Konva.Group {
  const g = new Konva.Group();
  g.setAttr('wireUid', w.uid);
  g.setAttr('nodeKind', 'wire');

  const { def, state } = wireAsPin(w);
  const path = computePinPath3D(def, state);
  if (path.length < 2) return g;

  // Suavizado con filete en cada codo (radio proporcional al grosor).
  const filletR = Math.max(0.30, WIRE_DIAMETER_MM * 1.2);
  const smoothed = buildSmoothPolyline(path.map((v) => v.position), filletR);
  const positions: { x: number; y: number }[] = [];
  for (let i = 0; i < smoothed.length; i += 2) {
    positions.push({ x: smoothed[i]!, y: smoothed[i + 1]! });
  }
  if (positions.length < 2) return g;

  // Perpendicular en cada vértice (bisector escalado 1/cos(θ/2)) para
  // mantener el ancho constante en los codos.
  const perps: { x: number; y: number }[] = positions.map((_, i) => {
    let p1x = 0, p1y = 0, p2x = 0, p2y = 0;
    if (i > 0) {
      const dx = positions[i]!.x - positions[i - 1]!.x;
      const dy = positions[i]!.y - positions[i - 1]!.y;
      const l = Math.hypot(dx, dy) || 1;
      p1x = -dy / l; p1y = dx / l;
    }
    if (i < positions.length - 1) {
      const dx = positions[i + 1]!.x - positions[i]!.x;
      const dy = positions[i + 1]!.y - positions[i]!.y;
      const l = Math.hypot(dx, dy) || 1;
      p2x = -dy / l; p2y = dx / l;
    }
    if (i === 0) return { x: p2x, y: p2y };
    if (i === positions.length - 1) return { x: p1x, y: p1y };
    let bx = p1x + p2x, by = p1y + p2y;
    const bLen = Math.hypot(bx, by);
    if (bLen < 1e-6) return { x: p1x, y: p1y };
    bx /= bLen; by /= bLen;
    const cosHalf = bx * p1x + by * p1y;
    const scale = Math.abs(cosHalf) > 0.1 ? 1 / cosHalf : 1;
    return { x: bx * scale, y: by * scale };
  });

  // Paleta cobre.
  const copperDark = '#5a3410';
  const copperShade = '#8a4d1d';
  const copperLight = '#eab578';
  const edge = copperDark;
  const gradStops = [
    0, edge,
    0.18, copperShade,
    0.5, copperLight,
    0.82, copperShade,
    1, edge,
  ];

  const w2 = WIRE_DIAMETER_MM / 2;

  // Un quad por segmento con gradiente perpendicular a SU dirección
  // (color estable pese a codos).
  for (let i = 0; i < positions.length - 1; i++) {
    const p0 = positions[i]!;
    const p1 = positions[i + 1]!;
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const segLen = Math.hypot(dx, dy);
    if (segLen < 1e-6) continue;
    const perpSegX = -dy / segLen;
    const perpSegY = dx / segLen;
    const midX = (p0.x + p1.x) / 2;
    const midY = (p0.y + p1.y) / 2;
    const quad = new Konva.Line({
      points: [
        p0.x + perps[i]!.x * w2, p0.y + perps[i]!.y * w2,
        p1.x + perps[i + 1]!.x * w2, p1.y + perps[i + 1]!.y * w2,
        p1.x - perps[i + 1]!.x * w2, p1.y - perps[i + 1]!.y * w2,
        p0.x - perps[i]!.x * w2, p0.y - perps[i]!.y * w2,
      ],
      closed: true,
      fillLinearGradientStartPoint: {
        x: midX + perpSegX * w2,
        y: midY + perpSegY * w2,
      },
      fillLinearGradientEndPoint: {
        x: midX - perpSegX * w2,
        y: midY - perpSegY * w2,
      },
      fillLinearGradientColorStops: gradStops,
      listening: true,
      // Colchón mínimo: el cable es fino (0.7 mm) y sin nada cuesta
      // atinar, pero un halo grande "roba" clicks destinados a zona
      // vacía y deja los handles enganchados a la selección.
      hitStrokeWidth: 0.5,
    });
    quad.setAttr('nodeKind', 'wire');
    quad.setAttr('wireUid', w.uid);
    g.add(quad);
  }

  // Contorno único como polilínea cerrada por encima (sólo stroke).
  const outlinePoints: number[] = [];
  for (let i = 0; i < positions.length; i++) {
    outlinePoints.push(
      positions[i]!.x + perps[i]!.x * w2,
      positions[i]!.y + perps[i]!.y * w2,
    );
  }
  for (let i = positions.length - 1; i >= 0; i--) {
    outlinePoints.push(
      positions[i]!.x - perps[i]!.x * w2,
      positions[i]!.y - perps[i]!.y * w2,
    );
  }
  g.add(new Konva.Line({
    points: outlinePoints,
    closed: true,
    stroke: edge,
    strokeWidth: 0.06,
    lineJoin: 'round',
    listening: false,
  }));

  // Handles de los extremos: sólo se dibujan cuando el cable está
  // seleccionado (marksVisible=true). SON PURAMENTE VISUALES —
  // listening=false — para que un click cercano NO cuente como hit del
  // handle. La detección de "arrastrar por el extremo" se hace por
  // PROXIMIDAD en onMouseDown (`endpointHitFor`) sólo cuando el click
  // ya cae en zona vacía; así clicks fuera de los extremos limpian la
  // selección con normalidad.
  if (showBendMarks && marksVisible) {
    const first = positions[0]!;
    const last = positions[positions.length - 1]!;
    for (const p of [first, last]) {
      g.add(new Konva.Circle({
        x: p.x,
        y: p.y,
        radius: 0.9,
        fill: '#ffffff',
        stroke: '#1976d2',
        strokeWidth: 0.25,
        listening: false,
      }));
    }
  }

  // Marcas de doblez (misma técnica que en pines): añadidas cuando
  // el cable está seleccionado. Cada marca lleva `wireUid` en lugar
  // de `pinId` para que el hit-testing la identifique como bend de
  // cable.
  if (showBendMarks) {
    const marks = renderBendMarks(def, state);
    // renderBendMarks pone `pinId` en el grupo y en cada tick; lo
    // sustituimos por `wireUid` para que findHit devuelva un hit de
    // tipo 'wire-bend'.
    marks.setAttr('wireUid', w.uid);
    marks.setAttr('pinId', undefined);
    marks.getChildren().forEach((tick) => {
      tick.setAttr('wireUid', w.uid);
      tick.setAttr('pinId', undefined);
      // El cable es MUY fino (0.48 mm); el hit-area derivado del
      // diámetro sería sólo ~1.2 mm y muy difícil de acertar con el
      // ratón. Forzamos un área generosa para los ticks de doblez.
      if (tick instanceof Konva.Line) {
        tick.hitStrokeWidth(3);
        // Marca invisible pero clicable cuando el cable no está
        // seleccionado: opacity 0 no afecta al hit-canvas de Konva
        // mientras listening siga activo.
        if (!marksVisible) tick.opacity(0);
      }
    });
    g.add(marks);
  }

  return g;
}
